import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, Flag, Trophy, Share2 } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { formatTimeCs } from "@/lib/time";
import { SUIT_SYMBOL, SUIT_COLOR, SUIT_LABEL } from "@/lib/suits";
import { Empty } from "@/components/Empty";
import { BottomTab } from "@/components/BottomTab";
import { ShareResultModal } from "@/components/ShareResultModal";

const TABS = [
  ["workout", "Ranking Workout"],
  ["games", "Games"],
  ["finals", "Finals"],
];

const ClockLine = ({ warmup, race }) => (
  <span className="text-[13px] text-onsurface3 flex items-center gap-1 flex-wrap">
    <Clock size={13} /> Warm up {warmup || "—"} · Gara {race || "—"}
  </span>
);

const ShareIcon = ({ onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-8 h-8 rounded-full bg-surface flex items-center justify-center text-onsurface3 shrink-0" aria-label="Condividi">
    <Share2 size={15} />
  </button>
);

const ColHeader = ({ third, thirdClass = "w-16" }) => (
  <div className="flex items-center gap-3 bg-surface2 rounded-2xl px-3 py-2 text-[11px] font-black tracking-widest text-onsurface3 mt-1">
    <div className="w-8">#</div>
    <div className="flex-1">ATLETA</div>
    <div className="w-10 text-center">LANE</div>
    <div className={`${thirdClass} text-right leading-tight`}>{third}</div>
    <div className="w-8" />
  </div>
);

const SuitHeader = ({ suit, count }) => (
  <div className="flex items-center gap-2 mt-6 mb-1">
    <span style={{ color: SUIT_COLOR[suit] }} className="text-xl leading-none">{SUIT_SYMBOL[suit]}</span>
    <span className="font-black tracking-[0.2em] text-sm text-onsurface">{SUIT_LABEL[suit]}</span>
    <div className="flex-1 h-px bg-surface3" />
    <span className="bg-surface3 text-onsurface3 rounded-md px-2 py-0.5 text-sm font-black">{count}</span>
  </div>
);

// ------------------------------ Ranking Workout ------------------------------
function WorkoutView({ gender, onShare }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/workout-ranking?gender=${gender}`).then((r) => setData(r.data)).catch(() => {});
  }, [gender]);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  if (!data) return null;
  const totalRows = data.single ? data.rows.length : (data.red.rows.length + data.black.rows.length);
  if (totalRows === 0) {
    return <Empty icon={<Flag size={54} />} title="Nessun atleta inserito" subtitle="Torna il giorno della gara" testid="workout-empty" />;
  }

  const SuitGroups = ({ rows }) => {
    const groups = [];
    rows.forEach((r) => {
      const last = groups[groups.length - 1];
      if (last && last.suit === r.suit) last.rows.push(r);
      else groups.push({ suit: r.suit, rows: [r] });
    });
    return (
      <>
        {groups.map((g) => (
          <div key={g.suit || "senza"}>
            {g.suit ? <SuitHeader suit={g.suit} count={g.rows.length} /> : (
              <div className="flex items-center gap-2 mt-6 mb-1">
                <span className="font-black tracking-[0.2em] text-sm text-onsurface3">SENZA ROUND</span>
                <div className="flex-1 h-px bg-surface3" />
              </div>
            )}
            <ColHeader third="ROUND (+ REPS)" thirdClass="w-24" />
            <div className="mt-1">
              {g.rows.map((r) => (
                <div key={r.athlete_id} data-testid={`workout-row-${r.athlete_id}`} className="flex items-start gap-3 px-3 py-3 border-b border-surface3">
                  <div className="w-8 text-2xl font-black text-onsurface tabular-nums leading-none pt-0.5">{r.rank ? r.rank : "—"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-onsurface leading-tight break-words">{r.name}</p>
                    {r.instagram && <p className="text-[13px] text-onsurface3">@{r.instagram}</p>}
                    <ClockLine warmup={r.warmup_time} race={r.race_time} />
                  </div>
                  <div className="w-10 text-center font-black text-onsurface pt-1">{r.lane || "—"}</div>
                  <div className="w-24 text-right pt-0.5">
                    {r.workout_rounds != null ? (
                      <>
                        <span className="text-2xl font-black text-onsurface tabular-nums leading-none">{r.workout_rounds}</span>
                        <span className="text-sm font-bold text-onsurface3"> (+{r.workout_reps || 0})</span>
                      </>
                    ) : <span className="text-lg font-black text-onsurface">—</span>}
                  </div>
                  <div className="w-8 pt-0.5">
                    {r.rank ? <ShareIcon testid={`workout-share-${r.athlete_id}`} onClick={() => onShare(r.athlete_id)} /> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>
    );
  };

  const Division = ({ label, red, block }) => {
    if (block.rows.length === 0) return null;
    return (
      <div className="mt-5">
        <div className={`rounded-2xl px-5 py-4 flex items-center justify-between text-white ${red ? "bg-mberror" : "bg-black"}`}>
          <span className="text-2xl font-black tracking-[0.15em]">{label}</span>
          <span className="text-sm font-bold opacity-90">{block.total} atleti</span>
        </div>
        <SuitGroups rows={block.rows} />
      </div>
    );
  };

  return (
    <div data-testid="workout-view">
      {data.single ? (
        <div className="mt-3"><SuitGroups rows={data.rows} /></div>
      ) : (
        <>
          <Division label="RED" red block={data.red} />
          <Division label="BLACK" red={false} block={data.black} />
        </>
      )}
    </div>
  );
}

// ------------------------------ Games ------------------------------
function GamesView({ gender, onShare }) {
  const [data, setData] = useState(null);
  const [suit, setSuit] = useState(null);
  const load = useCallback(() => {
    api.get(`/games?gender=${gender}`).then((r) => setData(r.data)).catch(() => {});
  }, [gender]);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    if (data && data.suits.length) {
      if (!data.suits.find((s) => s.suit === suit)) setSuit(data.suits[0].suit);
    }
  }, [data, suit]);

  if (!data) return null;
  if (data.suits.length === 0) {
    return <Empty icon={<Flag size={54} />} title="Nessun atleta inserito" subtitle="Torna il giorno della gara" testid="games-empty" />;
  }
  const current = data.suits.find((s) => s.suit === suit) || data.suits[0];

  return (
    <div data-testid="games-view">
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 pt-1">
        {data.suits.map((s) => {
          const active = s.suit === current.suit;
          return (
            <button key={s.suit} data-testid={`games-suit-${s.suit}`} onClick={() => setSuit(s.suit)}
              className={`shrink-0 rounded-full px-5 py-2.5 font-black flex items-center gap-2 ${active ? "bg-black text-brand" : "bg-surface3 text-onsurface"}`}>
              <span style={{ color: active && SUIT_COLOR[s.suit] === "#111111" ? "#F5D400" : SUIT_COLOR[s.suit] }}>{SUIT_SYMBOL[s.suit]}</span>
              {SUIT_LABEL[s.suit].charAt(0) + SUIT_LABEL[s.suit].slice(1).toLowerCase()}
            </button>
          );
        })}
      </div>

      {current.batterie.map((b) => (
        <div key={b.batteria_index} className="mt-3">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <span className="bg-black text-brand rounded-full px-4 py-1.5 text-sm font-black tracking-wide">BATTERIA {b.batteria_index + 1}</span>
            <ClockLine warmup={b.warmup_time} race={b.race_time} />
          </div>
          <ColHeader third="PTS" />
          <div className="mt-2 space-y-3">
            {b.athletes.map((a) => (
              <div key={a.athlete_id} data-testid={`games-card-${a.athlete_id}`} className="bg-surface2 rounded-2xl p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 text-2xl font-black text-onsurface leading-none">{a.is_finalist ? "👑" : a.total_position}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-onsurface leading-tight break-words">{a.name}</p>
                    {a.instagram && <p className="text-[13px] text-onsurface3">@{a.instagram}</p>}
                  </div>
                  <div className="w-10 text-center font-black text-onsurface">{a.lane || "—"}</div>
                  <div className="bg-black rounded-xl px-4 py-2 min-w-[56px] text-center">
                    <span className="text-brand text-2xl font-black tabular-nums">{a.total_points}</span>
                  </div>
                  <ShareIcon testid={`games-share-${a.athlete_id}`} onClick={() => onShare(a.athlete_id)} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {a.games.map((g) => (
                    <div key={g.game_number} className="bg-black rounded-xl py-2 text-center">
                      <div className="text-[10px] text-onsurface3 font-black tracking-wider">G{g.game_number}</div>
                      <div className="text-white font-bold tabular-nums text-sm my-0.5">{g.game_number === 2 ? (g.time_cs != null ? `${g.time_cs} kg` : "—") : formatTimeCs(g.time_cs)}</div>
                      <div className="text-brand text-xs font-black">{g.points}pt</div>
                      <div className="text-[10px] text-onsurface3 font-bold mt-0.5">Lane {g.lane || a.lane || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------ Finals ------------------------------
function FinalsView({ gender, onShare }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/finals?gender=${gender}`).then((r) => setData(r.data)).catch(() => {});
  }, [gender]);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  if (!data) return null;
  if (data.suits.length === 0) {
    return <Empty icon={<Trophy size={54} />} title="Nessun finalista" subtitle="Torna il giorno della gara" testid="finals-empty" />;
  }

  return (
    <div data-testid="finals-view">
      {data.suits.map((s) => (
        <div key={s.suit} className="mt-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <span className="bg-brand text-onbrand rounded-full px-4 py-1.5 text-sm font-black tracking-wide flex items-center gap-2">
              <span style={{ color: SUIT_COLOR[s.suit] === "#111111" ? "#000" : SUIT_COLOR[s.suit] }}>{SUIT_SYMBOL[s.suit]}</span>
              FINALE {SUIT_LABEL[s.suit]}
            </span>
            <ClockLine warmup={s.warmup_time} race={s.race_time} />
          </div>
          <ColHeader third="TEMPO" />
          <div className="mt-1">
            {s.finalists.map((f) => {
              const podium = f.rank >= 1 && f.rank <= 3;
              return (
                <div key={f.athlete_id} data-testid={`finals-row-${f.athlete_id}`} className="flex items-start gap-3 px-3 py-3 border-b border-surface3">
                  <div className={`w-8 text-2xl font-black tabular-nums leading-none pt-0.5 ${podium ? "text-brand" : "text-onsurface"}`}>{f.rank ? f.rank : "—"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-onsurface leading-tight break-words">{f.name}</p>
                    {f.instagram && <p className="text-[13px] text-onsurface3">@{f.instagram}</p>}
                    <p className="text-[13px] text-onsurface3">batt. {f.batteria_index + 1}</p>
                  </div>
                  <div className="w-10 text-center font-black text-onsurface pt-1">{f.lane || "—"}</div>
                  <div className="w-16 text-right text-lg font-black text-onsurface tabular-nums pt-0.5">{formatTimeCs(f.time_cs)}</div>
                  <div className="w-8 pt-0.5"><ShareIcon testid={`finals-share-${f.athlete_id}`} onClick={() => onShare(f.athlete_id)} /></div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------ Page ------------------------------
export default function Classifica() {
  const nav = useNavigate();
  const [gender, setGender] = useState("Men");
  const [tab, setTab] = useState("workout");
  const [shareId, setShareId] = useState(null);

  return (
    <div className="min-h-screen pb-24 bg-surface" data-testid="classifica-page">
      <header className="bg-black px-4 pb-4" style={{ paddingTop: "calc(var(--safe-top) + 16px)" }}>
        <div className="flex items-center justify-between">
          <button data-testid="classifica-back-btn" onClick={() => nav("/event")} className="text-white"><ChevronLeft size={32} strokeWidth={2.5} /></button>
          <h1 className="text-2xl font-black text-white">Classifica</h1>
          <div data-testid="live-badge" className="flex items-center gap-1.5 bg-mberror/20 border border-mberror/40 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-mberror animate-livepulse" />
            <span className="text-mberror font-black text-sm tracking-wide">LIVE</span>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex gap-2 bg-[#1c1c1e] p-1 rounded-full">
            {[["Men", "Uomo"], ["Women", "Donna"]].map(([v, l]) => (
              <button key={v} data-testid={`classifica-gender-${v.toLowerCase()}`} onClick={() => setGender(v)}
                className={`flex-1 py-2.5 rounded-full font-bold text-base ${gender === v ? "bg-brand text-onbrand" : "text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
      </header>

      <div className="bg-surface2 px-4 pt-3 flex gap-6 border-b border-surface3">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`classifica-tab-${v}`} onClick={() => setTab(v)}
            className={`pb-2 font-black text-lg leading-tight text-left ${tab === v ? "text-onsurface border-b-[3px] border-brand" : "text-onsurface3"}`}>
            {l.split(" ").map((w) => <div key={w}>{w}</div>)}
          </button>
        ))}
      </div>

      <div className="px-4 pt-2">
        {tab === "workout" && <WorkoutView gender={gender} onShare={setShareId} />}
        {tab === "games" && <GamesView gender={gender} onShare={setShareId} />}
        {tab === "finals" && <FinalsView gender={gender} onShare={setShareId} />}
      </div>

      <ShareResultModal open={!!shareId} onClose={() => setShareId(null)} athleteId={shareId} />
      <Toaster position="top-center" richColors />
      <BottomTab />
    </div>
  );
}
